import loaderGif from "../../Assests/loader.gif";
import { VendorNotesColumns } from "../../ColumnsConfig/VendorsColumn";
import Table from "../../Components/Table";
import { fetchCommodityCodes } from "../../Services/RoutingGuidesDetails";
import {
  fetchVendorNotes,
  createVendorNote,
  deleteVendorNote,
  updateVendorNote,
} from "../../Services/VendorNotes";
import { fetchVendors, updateVendor } from "../../Services/Vendors";
import {
  Box,
  Button,
  Grid2,
  Typography,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import { useParams } from "react-router-dom";
import "react-notifications/lib/notifications.css";
import ConfirmationModal from "../../Common/ConfirmationModal";

const SupplierDetail = () => {
  const { supplierSiteId } = useParams();
  const [supplierDetail, setSupplierDetail] = useState(null);
  const [openAddNote, setOpenAddNote] = useState(false);
  const [openEditVendor, setOpenEditVendor] = useState(false);
  const [vendorNotesData, setVendorNotesData] = useState([]);
  const [formValues, setFormValues] = useState({
    contact: "",
    phone: "",
    fax: "",
    note: "",
  });
  const [vendorEditValues, setVendorEditValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editNoteIndex, setEditNoteIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortColumn] = useState("vendor_id");
  const [order, setSortOrder] = useState("asc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [commodityCodes, setCommodityCodes] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const FOB_TERMS_OPTIONS = [
    { value: "Origin", label: "Origin" },
    { value: "Consolidator", label: "Consolidator" },
    { value: "Destination", label: "Destination" },
    { value: "", label: "" },
  ];

  const fetchVendorDetails = async (vendorId, page, size) => {
    try {
      setLoading(true);
      const { notes, totalRecords } = await fetchVendorNotes(
        vendorId,
        page,
        size
      );
      setVendorNotesData(notes);
      setTotalRecords(totalRecords);
    } catch (error) {
      console.error("Error fetching vendor notes:", error);
      NotificationManager.error("Failed to fetch vendor notes");
    } finally {
      setLoading(false);
    }
  };

  const refreshVendorData = async () => {
    try {
      setLoading(true);
      const [vendorsResponse] = await Promise.all([
        fetchVendors({ vendorId: supplierSiteId }, 1, 1, "", "asc"),
      ]);

      const vendors = vendorsResponse.data || vendorsResponse;
      const updatedSupplier = vendors.find(
        (v) => v.vendor_id?.toString() === supplierSiteId
      );

      if (!updatedSupplier) {
        throw new Error(`No supplier found for ID: ${supplierSiteId}`);
      }

      setSupplierDetail(updatedSupplier);
      setVendorEditValues(updatedSupplier);

      // Also refresh notes
      const { notes, totalRecords } = await fetchVendorNotes(
        updatedSupplier.vendor_id,
        currentPage,
        pageSize
      );
      setVendorNotesData(notes);
      setTotalRecords(totalRecords);
    } catch (error) {
      console.error("Error refreshing vendor data:", error);
      NotificationManager.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch - only runs when supplierSiteId changes
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [commodityCodesData] = await Promise.all([fetchCommodityCodes()]);
        setCommodityCodes(commodityCodesData);
        await refreshVendorData();
      } catch (error) {
        console.error("Error fetching initial data:", error);
        NotificationManager.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [supplierSiteId]);

  // Separate useEffect for pagination changes
  useEffect(() => {
    if (supplierDetail?.vendor_id) {
      fetchVendorDetails(supplierDetail.vendor_id, currentPage, pageSize);
    }
  }, [currentPage, pageSize]);

  if (!supplierDetail) {
    return (
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgcolor="rgba(255, 255, 255, 0.8)"
        zIndex={1}
      >
        <img src={loaderGif} alt="Loading..." width={50} height={50} />
      </Box>
    );
  }

  const supplierFields = [
    { label: "Supplier Site Id", value: supplierDetail.supplier_site_id },
    { label: "Department Number", value: supplierDetail.dept_code },
    { label: "Vendor Name", value: supplierDetail.vendor_name },
    {
      label: "Commodity Code",
      value: supplierDetail.commodity_code
        ? `${supplierDetail.commodity_code} - ${
            supplierDetail.commodity_code_name || ""
          }`
        : "",
    },
    { label: "FOB Terms", value: supplierDetail.fob_terms },
    { label: "Chain", value: supplierDetail.chain },
    { label: "Area", value: supplierDetail.area },
    { label: "Brand ID", value: supplierDetail.brand_id },
    { label: "Brand Description", value: supplierDetail.brand_desc },
  ];

  const handleSort = (column, order) => {
    setSortColumn(column);
    setSortOrder(order);
  };

  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    setPageSize(newPageSize || pageSize);
  };

  const handleAddNoteDialogOpen = () => {
    setFormValues({
      contact: "",
      phone: "",
      fax: "",
      note: "",
    });
    setIsEditMode(false);
    setOpenAddNote(true);
  };

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete) return;

    try {
      setLoading(true);
      await deleteVendorNote(noteToDelete.vendor_id, noteToDelete.note_id);

      // Remove the deleted note from the current state
      setVendorNotesData((prevNotes) =>
        prevNotes.filter((note) => note.note_id !== noteToDelete.note_id)
      );

      // Adjust totalRecords
      setTotalRecords((prev) => prev - 1);

      // Check if we need to go to previous page
      if (vendorNotesData.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        // Refresh the current page data
        const { notes, totalRecords } = await fetchVendorNotes(
          noteToDelete.vendor_id,
          currentPage,
          pageSize
        );
        setVendorNotesData(notes);
        setTotalRecords(totalRecords);
      }

      NotificationManager.success("Note deleted successfully");
    } catch (error) {
      console.error("Error deleting note:", error);
      NotificationManager.error(error.message || "Failed to delete note");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
    }
  };

  const handleEditNoteDialogOpen = (note, index) => {
    setIsEditMode(true);
    setEditNoteIndex(index);
    setFormValues(note);
    setOpenAddNote(true);
  };

  const handleDialogClose = () => {
    setOpenAddNote(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleVendorInputChange = (e) => {
    const { name, value } = e.target;
    console.log("Changing field:", name, "to value:", value); // Debug log
    setVendorEditValues((prev) => {
      const newValues = {
        ...prev,
        [name]: value,
      };
      console.log("New vendor edit values:", newValues); // Debug log
      return newValues;
    });
  };
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const vendorId = vendorEditValues.vendor_id;

      if (isEditMode) {
        await updateVendorNote(vendorId, formValues.note_id, formValues);
        NotificationManager.success("Note updated successfully");
      } else {
        const newNote = {
          supplierSiteId: supplierDetail.supplier_site_id,
          deptNumber: supplierDetail.dept_code,
          contact: formValues.contact,
          phone: formValues.phone,
          faxNumber: formValues.fax,
          note: formValues.note,
        };

        await createVendorNote(vendorId, newNote);
        NotificationManager.success("Note created successfully");
      }

      // Refresh the notes data after submission
      const { notes, totalRecords } = await fetchVendorNotes(
        vendorId,
        currentPage,
        pageSize
      );
      setVendorNotesData(notes);
      setTotalRecords(totalRecords);
      handleDialogClose();
    } catch (error) {
      console.error("Failed to submit note:", error);
      NotificationManager.error(error.message || "Failed to save note");
    } finally {
      setLoading(false);
    }
  };

  const handleEditVendorDialogOpen = () => {
    // cheeck mandatory fields are filled or not

    setVendorEditValues({
      ...supplierDetail,
      commodity_code_id: supplierDetail.commodity_code_id, // Ensure this is set
    });
    setOpenEditVendor(true);
  };

  // Update the handleEditVendorSubmit function
  const handleEditVendorSubmit = async () => {
    try {
      if (
        !vendorEditValues.chain ||
        vendorEditValues.area === undefined ||
        vendorEditValues.area === null ||
        vendorEditValues.area === "" || // Changed this line
        !vendorEditValues.brand_id ||
        !vendorEditValues.brand_desc
      ) {
        NotificationManager.error("Please fill mandatory fields");
        return;
      }
  
      setLoading(true);
      const updatePayload = {
        ...vendorEditValues,
        area: vendorEditValues.area.toString(),
      };
  
      await updateVendor(vendorEditValues.vendor_id, updatePayload);
      await refreshVendorData();
  
      NotificationManager.success("Vendor updated successfully");
      setOpenEditVendor(false);
    } catch (error) {
      console.error("Error updating vendor:", error);
      NotificationManager.error(error.message || "Failed to update vendor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="px-5 my-3" sx={{ borderRadius: "8px", padding: "5px" }}>
      <NotificationContainer />
      <Paper
        elevation={3}
        sx={{ padding: "20px", backgroundColor: "#3f506b", color: "white" }}
      >
        <Typography variant="h4" gutterBottom color="white">
          Vendor
        </Typography>

        <Grid2 container spacing={2} alignItems="center">
          {supplierFields.map((field, index) => (
            <React.Fragment key={index}>
              <Grid2 item xs={3}>
                <Typography
                  variant="body1"
                  color="white"
                  sx={{ textAlign: "right" }}
                >
                  <strong>{field.label}:</strong>
                </Typography>
              </Grid2>
              <Grid2 item xs={8}>
                <Typography variant="body1" color="white">
                  {field.value}
                </Typography>
              </Grid2>
            </React.Fragment>
          ))}
        </Grid2>
      </Paper>

      <Button
        variant="contained"
        sx={{
          backgroundColor: "#3f506b",
          textTransform: "none",
          marginTop: "20px",
        }}
        onClick={handleEditVendorDialogOpen}
      >
        Edit Vendor
      </Button>

      <Typography variant="h4" sx={{ padding: "5px", marginTop: "20px" }}>
        Vendor Notes
      </Typography>
      <Table
        rows={vendorNotesData}
        columns={VendorNotesColumns(
          handleEditNoteDialogOpen,
          handleDeleteClick
        )} // Changed to handleDeleteClick
        paginationMode="server"
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onSort={handleSort}
      />

      <Button
        variant="contained"
        sx={{
          backgroundColor: "#3f506b",
          textTransform: "none",
          marginTop: "20px",
        }}
        onClick={handleAddNoteDialogOpen}
      >
        Add New
      </Button>

      <Dialog
        open={openAddNote}
        onClose={handleDialogClose}
        PaperProps={{
          sx: {
            backgroundColor: "white",
            color: "black",
          },
        }}
      >
        <DialogTitle>
          {isEditMode ? "Edit Vendor Note" : "Add Vendor Note"}
        </DialogTitle>
        <DialogContent>
          {loading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              justifyContent="center"
              alignItems="center"
              bgcolor="rgba(255, 255, 255, 0.8)"
              zIndex={9999}
            >
              <img src={loaderGif} alt="Loading..." width={50} height={50} />
            </Box>
          )}
          <Grid2 container spacing={2}>
            <Grid2 item xs={12}>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body1"
                  sx={{ mr: 1, width: "150px", fontWeight: "bold" }}
                >
                  Supplier Site Id:
                </Typography>
                <Typography variant="body1" sx={{ width: "230px" }}>
                  {supplierDetail.supplier_site_id}
                </Typography>
              </Box>
            </Grid2>
            <Grid2 item xs={12}>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body1"
                  sx={{ mr: 1, width: "150px", fontWeight: "bold" }}
                >
                  Vendor Name:
                </Typography>
                <Typography variant="body1" sx={{ width: "230px" }}>
                  {supplierDetail.vendor_name}
                </Typography>
              </Box>
            </Grid2>

            <Grid2 item xs={12}>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body1"
                  sx={{ mr: 1, width: "150px", fontWeight: "bold" }}
                >
                  Department Number:
                </Typography>
                <TextField
                  name="deptNumber"
                  variant="outlined"
                  fullWidth
                  value={supplierDetail.dept_code}
                  onChange={handleInputChange}
                  sx={{
                    width: "230px",
                    "& .MuiOutlinedInput-root": { height: "40px" },
                  }}
                />
              </Box>
            </Grid2>

            <Grid2 item xs={12}>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body1"
                  sx={{ mr: 1, width: "150px", fontWeight: "bold" }}
                >
                  Contact:
                </Typography>
                <TextField
                  name="contact"
                  variant="outlined"
                  fullWidth
                  value={formValues.contact}
                  onChange={handleInputChange}
                  sx={{
                    width: "230px",
                    "& .MuiOutlinedInput-root": { height: "40px" },
                  }}
                />
              </Box>
            </Grid2>

            <Grid2 item xs={12}>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body1"
                  sx={{ mr: 1, width: "150px", fontWeight: "bold" }}
                >
                  Phone:
                </Typography>
                <TextField
                  name="phone"
                  variant="outlined"
                  fullWidth
                  value={formValues.phone}
                  onChange={handleInputChange}
                  sx={{
                    width: "230px",
                    "& .MuiOutlinedInput-root": { height: "40px" },
                  }}
                />
              </Box>
            </Grid2>

            <Grid2 item xs={12}>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body1"
                  sx={{ mr: 1, width: "150px", fontWeight: "bold" }}
                >
                  Fax:
                </Typography>
                <TextField
                  name="fax"
                  variant="outlined"
                  fullWidth
                  value={formValues.fax}
                  onChange={handleInputChange}
                  sx={{
                    width: "230px",
                    "& .MuiOutlinedInput-root": { height: "40px" },
                  }}
                />
              </Box>
            </Grid2>

            <Grid2 item xs={12}>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body1"
                  sx={{
                    mr: 1,
                    width: "150px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Note:
                  <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                </Typography>
                <TextField
                  name="note"
                  variant="outlined"
                  fullWidth
                  value={formValues.note}
                  onChange={handleInputChange}
                  error={!formValues.note} // Shows error state if the field is empty
                  helperText={!formValues.note ? "This field is required" : ""} // Error message
                  sx={{
                    width: "230px",
                    "& .MuiOutlinedInput-root": { height: "40px" },
                  }}
                />
              </Box>
            </Grid2>
          </Grid2>
          <Grid2 item xs={12} sx={{ mt: 2 }}>
            <Box display="flex" justifyContent="flex-start">
              <Button
                onClick={handleDialogClose}
                sx={{
                  backgroundColor: "#3f506b",
                  color: "white",
                  marginRight: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                sx={{ backgroundColor: "#3f506b", color: "white" }}
              >
                Save
              </Button>
            </Box>
          </Grid2>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        message="Are you sure you want to delete this Vendor Note?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Edit Vendor Details Dialog */}
      <Dialog
        open={openEditVendor}
        onClose={() => setOpenEditVendor(false)}
        PaperProps={{
          sx: {
            backgroundColor: "white",
            color: "black",
          },
        }}
      >
        <DialogTitle>Edit Vendor Details</DialogTitle>
        <DialogContent>
          {loading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              justifyContent="center"
              alignItems="center"
              bgcolor="rgba(255, 255, 255, 0.8)"
              zIndex={9999}
            >
              <img src={loaderGif} alt="Loading..." width={50} height={50} />
            </Box>
          )}
          <Grid2 container spacing={2}>
            {[
              {
                label: "Supplier Site Id",
                name: "supplier_site_id",
                editable: false,
              },
              {
                label: "Department Number",
                name: "dept_code",
                editable: false,
              },
              { label: "Vendor Name", name: "vendor_name", editable: false },
              { label: "Address", name: "address", editable: false },
              { label: "Address cont.", name: "address_cont", editable: false },
              { label: "City", name: "city", editable: false },
              { label: "State", name: "state", editable: false },
              { label: "Zip", name: "zip", editable: false },
              {
                label: "Commodity Code",
                name: "commodity_code_id",
                editable: true,
                isSelect: true,
                mandatory: true,
              },
              {
                label: "FOB Terms",
                name: "fob_terms",
                editable: true,
                isSelect: true,
                options: FOB_TERMS_OPTIONS,
              },
              {
                label: "Chain",
                name: "chain",
                editable: true,
                mandatory: true,
              },
              { label: "Area", name: "area", editable: true, mandatory: true },
              {
                label: "Brand ID",
                name: "brand_id",
                editable: true,
                mandatory: true,
              },
              {
                label: "Brand Description",
                name: "brand_desc",
                editable: true,
                mandatory: true,
              },
            ].map(({ label, name, editable, isSelect, options, mandatory }) => {
              // Special handling for area field - don't show error if value is 0
              const hasError =
                mandatory &&
                (name === "area"
                  ? !vendorEditValues[name] && vendorEditValues[name] !== 0
                  : !vendorEditValues[name]);

              return (
                <Grid2 item xs={12} key={name}>
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="body1"
                      sx={{ mr: 1, width: "150px", fontWeight: "bold" }}
                    >
                      {label}:
                    </Typography>
                    {isSelect ? (
                      <Select
                        name={name}
                        value={vendorEditValues[name] || ""}
                        onChange={handleVendorInputChange}
                        disabled={!editable}
                        fullWidth
                        sx={{
                          width: "230px",
                          "& .MuiOutlinedInput-root": { height: "40px" },
                        }}
                        error={hasError}
                      >
                        {options
                          ? options.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))
                          : commodityCodes.map((code) => (
                              <MenuItem
                                key={code.commodity_code_id}
                                value={code.commodity_code_id}
                              >
                                {code.commodity_code} - {code.name}
                              </MenuItem>
                            ))}
                      </Select>
                    ) : (
                      <TextField
                        name={name}
                        variant="outlined"
                        fullWidth
                        value={
                          vendorEditValues[name] !== undefined
                            ? vendorEditValues[name]
                            : ""
                        }
                        onChange={handleVendorInputChange}
                        disabled={!editable}
                        sx={{
                          width: "230px",
                          "& .MuiOutlinedInput-root": { height: "40px" },
                        }}
                        error={hasError}
                        helperText={hasError ? "This field is required" : ""}
                      />
                    )}
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>

          <Grid2 item xs={12} sx={{ mt: 2 }}>
            <Box display="flex" justifyContent="flex-start">
              <Button
                onClick={() => setOpenEditVendor(false)}
                sx={{
                  backgroundColor: "#3f506b",
                  color: "white",
                  marginRight: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditVendorSubmit}
                sx={{ backgroundColor: "#3f506b", color: "white" }}
              >
                Save
              </Button>
            </Box>
            <Box position="relative" mt={2}>
              {loading && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  bgcolor="rgba(255, 255, 255, 0.8)"
                  zIndex={1}
                >
                  <img
                    src={loaderGif}
                    alt="Loading..."
                    width={50}
                    height={50}
                  />
                </Box>
              )}
            </Box>
          </Grid2>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupplierDetail;
